import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Button,
    Empty,
    message,
    Modal,
    PageHeader,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd'
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CloudDownloadOutlined,
    DeleteOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    MailOutlined,
    MoneyCollectOutlined,
    ReloadOutlined,
    SnippetsOutlined,
    SyncOutlined
} from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { format } from 'date-fns'

import './Accounting.scss'

import {
    deleteReport,
    downloadReport,
    listReports,
    ReportApiError,
    ReportRequest,
    ReportStatus
} from '../../Utils/reportsApi'

const POLL_INTERVAL_MS = 10000

/** Submenú de Gestión, compartido con la vista de Informes. */
export const accountingSubMenu = (isPeru: boolean) => [
    { key: 'index', name: 'Informes', icon: <SnippetsOutlined /> },
    isPeru ? {} : { key: 'emission', name: 'Facturación mensual', icon: <MoneyCollectOutlined /> },
    { key: 'reports', name: 'Reportes descargados', icon: <CloudDownloadOutlined /> },
]

const STATUS: Record<ReportStatus, { label: string, color: string, icon: JSX.Element }> = {
    pending: { label: 'En cola', color: 'default', icon: <ClockCircleOutlined /> },
    processing: { label: 'Generando', color: 'processing', icon: <SyncOutlined spin /> },
    done: { label: 'Listo', color: 'success', icon: <CheckCircleOutlined /> },
    failed: { label: 'Falló', color: 'error', icon: <CloseCircleOutlined /> },
    expired: { label: 'Expirado', color: 'default', icon: <ExclamationCircleOutlined /> },
}

const PARAM_LABELS: Record<string, string> = {
    onlyEan: 'Solo EAN',
    filterMarcas: 'Marcas',
    simplified: 'Simplificado',
    showCostos: 'Costos',
    periodo: 'Periodo',
    advanced: 'Completo',
    mes: 'Mes',
    year: 'Año',
    documentType: 'Documento',
    startAt: 'Desde',
    endsAt: 'Hasta',
    endAt: 'Hasta',
    parityAt: 'Paridad',
}

/** Fechas del backend: `Y-m-d H:i:s` en hora de Santiago (se interpretan como hora local). */
const parseDate = (value: string | null): Date | null => {
    if (!value) return null
    const date = new Date(value.replace(' ', 'T'))
    return isNaN(date.getTime()) ? null : date
}

const formatDate = (value: string | null) => {
    const date = parseDate(value)
    return date ? format(date, 'dd-MM-yyyy HH:mm') : '—'
}

const formatSize = (bytes: number | null) => {
    if (bytes === null || bytes === undefined) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const formatParamValue = (value: any): string => {
    if (Array.isArray(value)) {
        if (!value.length) return 'Todas'
        return value.length > 3 ? `${value.slice(0, 3).join(', ')} +${value.length - 3}` : value.join(', ')
    }
    if (typeof value === 'boolean') return value ? 'Sí' : 'No'
    if (value === null || value === undefined || value === '') return '—'
    return `${value}`
}

const summarizeParams = (params: Record<string, any> | null) => {
    const entries = Object.entries(params || {})
    if (!entries.length) return 'Sin parámetros'
    return entries.map(([key, value]) => `${PARAM_LABELS[key] || key}: ${formatParamValue(value)}`).join(' · ')
}

/** Estado efectivo: un `done` cuyo expires_at ya pasó se muestra como expirado. */
const effectiveStatus = (row: ReportRequest, now: number): ReportStatus => {
    const expires = parseDate(row.expires_at)
    if (row.status === 'done' && expires && expires.getTime() <= now) return 'expired'
    return row.status
}

const remainingLabel = (row: ReportRequest, now: number) => {
    const status = effectiveStatus(row, now)
    if (status === 'expired') return <Typography.Text type="secondary">Expirado</Typography.Text>
    if (status === 'failed') return '—'
    const expires = parseDate(row.expires_at)
    if (!expires) return <Typography.Text type="secondary">Al terminar: 48 h</Typography.Text>
    const diff = expires.getTime() - now
    const hours = Math.floor(diff / 3600000)
    const text = hours >= 1 ? `expira en ${hours} h` : `expira en ${Math.max(1, Math.ceil(diff / 60000))} min`
    return <Tooltip title={formatDate(row.expires_at)}>
        <Typography.Text type={hours < 6 ? 'warning' : undefined}>{text}</Typography.Text>
    </Tooltip>
}

function ReportHistory({ exposeSubMenu, setMainLoading }: any) {
    const { token, userInfo } = useSelector((state: any) => state)
    const isPeru = userInfo?.id_client === "11"
    const history = useHistory()

    const [requests, setRequests] = useState<ReportRequest[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [downloadingId, setDownloadingId] = useState<number | null>(null)
    const [now, setNow] = useState(Date.now())
    const mounted = useRef(true)

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    useEffect(() => {
        if (!!exposeSubMenu) {
            exposeSubMenu(accountingSubMenu(isPeru))
        }
    }, [exposeSubMenu, isPeru])

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            const data = await listReports(token)
            if (!mounted.current) return
            setRequests(data)
            setLoadError(null)
        } catch (err) {
            if (!mounted.current) return
            const msg = err instanceof ReportApiError ? err.message : 'No se pudo obtener el historial de reportes.'
            setLoadError(msg)
            if (!silent) message.error(msg)
        } finally {
            if (mounted.current) {
                setLoading(false)
                setNow(Date.now())
            }
        }
    }, [token])

    useEffect(() => {
        load().finally(() => setMainLoading && setMainLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Polling solo mientras haya solicitudes en cola o generándose.
    const hasActive = requests.some(r => r.status === 'pending' || r.status === 'processing')
    useEffect(() => {
        if (!hasActive) return
        const interval = window.setInterval(() => load(true), POLL_INTERVAL_MS)
        return () => window.clearInterval(interval)
    }, [hasActive, load])

    // Refresca el "expira en" cada minuto.
    useEffect(() => {
        const interval = window.setInterval(() => setNow(Date.now()), 60000)
        return () => window.clearInterval(interval)
    }, [])

    const handleDownload = async (row: ReportRequest) => {
        setDownloadingId(row.id)
        try {
            await downloadReport(token, row)
        } catch (err) {
            const apiErr = err instanceof ReportApiError ? err : null
            message.error(apiErr ? apiErr.message : 'No se pudo descargar el archivo.')
            if (apiErr && (apiErr.status === 410 || apiErr.status === 404)) {
                load(true)
            }
        } finally {
            if (mounted.current) setDownloadingId(null)
        }
    }

    const handleDelete = (row: ReportRequest) => {
        Modal.confirm({
            title: '¿Eliminar este reporte?',
            icon: <ExclamationCircleOutlined />,
            content: <>Se eliminará <strong>{row.report_label}</strong> del historial{row.file_name ? ' junto con su archivo' : ''}. Esta acción no se puede deshacer.</>,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteReport(token, row.id)
                    message.success('Reporte eliminado.')
                    if (mounted.current) setRequests(prev => prev.filter(r => r.id !== row.id))
                } catch (err) {
                    message.error(err instanceof ReportApiError ? err.message : 'No se pudo eliminar el reporte.')
                }
            }
        })
    }

    const columns = [
        {
            title: 'Reporte',
            dataIndex: 'report_label',
            key: 'report_label',
            render: (label: string, row: ReportRequest) => <>
                <strong>{label || row.report}</strong>
                {row.file_name && <><br /><Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.file_name}</Typography.Text></>}
            </>
        },
        {
            title: 'Parámetros',
            dataIndex: 'params',
            key: 'params',
            render: (params: Record<string, any> | null) => {
                const summary = summarizeParams(params)
                return <Tooltip title={summary}>
                    <Typography.Text ellipsis style={{ maxWidth: 240, display: 'inline-block' }}>{summary}</Typography.Text>
                </Tooltip>
            }
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (_: ReportStatus, row: ReportRequest) => {
                const status = STATUS[effectiveStatus(row, now)] || STATUS.pending
                return <>
                    <Tag color={status.color} icon={status.icon}>{status.label}</Tag>
                    {row.status === 'failed' && row.error && <div style={{ maxWidth: 260 }}>
                        <Typography.Text type="danger" style={{ fontSize: 12 }}>{row.error}</Typography.Text>
                    </div>}
                </>
            }
        },
        {
            title: 'Solicitado',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: string) => formatDate(value)
        },
        {
            title: 'Disponibilidad',
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: (_: string | null, row: ReportRequest) => remainingLabel(row, now)
        },
        {
            title: 'Tamaño',
            dataIndex: 'file_size',
            key: 'file_size',
            render: (value: number | null) => formatSize(value)
        },
        {
            title: 'Correo',
            dataIndex: 'emailed',
            key: 'emailed',
            render: (emailed: boolean, row: ReportRequest) => {
                if (emailed) {
                    return <Tooltip title={row.email ? `Enviado a ${row.email}` : 'Enviado'}>
                        <Tag color="green" icon={<MailOutlined />}>Enviado</Tag>
                    </Tooltip>
                }
                return row.status === 'done'
                    ? <Tag icon={<MailOutlined />}>No enviado</Tag>
                    : <Typography.Text type="secondary">—</Typography.Text>
            }
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_: any, row: ReportRequest) => {
                const canDownload = effectiveStatus(row, now) === 'done'
                return <Space>
                    <Tooltip title={canDownload ? 'Descargar' : 'Disponible cuando el reporte esté listo'}>
                        <Button
                            type="primary"
                            size="small"
                            icon={<DownloadOutlined />}
                            disabled={!canDownload}
                            loading={downloadingId === row.id}
                            onClick={() => handleDownload(row)}
                        >
                            Descargar
                        </Button>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
                    </Tooltip>
                </Space>
            }
        },
    ]

    return (
        <div className="App">
            <div className="Accounting">
                <PageHeader
                    className="site-page-header"
                    title="Reportes descargados"
                    subTitle="Cada archivo queda disponible por 48 h"
                    extra={[
                        <Button key="reload" icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>
                            Actualizar
                        </Button>
                    ]}
                />
                <div className="Wrapper">
                    <div className="Accounting--History">
                        {hasActive && <p className="Accounting--HistoryHint">
                            <SyncOutlined spin /> Hay reportes en proceso; esta lista se actualiza automáticamente.
                        </p>}
                        <Table
                            rowKey="id"
                            size="middle"
                            loading={loading && !requests.length}
                            dataSource={requests}
                            columns={columns}
                            pagination={requests.length > 20 ? { pageSize: 20 } : false}
                            scroll={{ x: true }}
                            locale={{
                                emptyText: <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={loadError
                                        ? <>No se pudo cargar el historial.<br />{loadError}</>
                                        : <>Aún no tienes reportes en las últimas 48 h.<br />Solicita uno desde <strong>Informes</strong> y aparecerá aquí.</>}
                                >
                                    <Button type="primary" onClick={() => history.push('/accounting')}>Ir a Informes</Button>
                                </Empty>
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ReportHistory
